import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { authService, triggerGlobalLogout } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Create axios instance with correct base URL
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track refresh state
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration and refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    console.error('API Error:', error.response?.status, error.config?.url, error.response?.data);
    
    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry the refresh endpoint itself
      if (originalRequest.url?.includes('/auth/refresh')) {
        isRefreshing = false;
        processQueue(error);
        localStorage.clear();
        triggerGlobalLogout();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Check if refresh token exists before attempting refresh
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No refresh token, immediately logout
        isRefreshing = false;
        processQueue(error);
        localStorage.clear();
        triggerGlobalLogout();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request with timeout
        return new Promise((resolve, reject) => {
          // Add timeout to prevent infinite waiting
          const timeout = setTimeout(() => {
            reject(new Error('Token refresh timeout'));
            // Force logout if refresh takes too long
            if (isRefreshing) {
              isRefreshing = false;
              localStorage.clear();
              triggerGlobalLogout();
              window.location.href = '/login';
            }
          }, 10000); // 10 second timeout
          
          failedQueue.push({ 
            resolve: (value) => {
              clearTimeout(timeout);
              resolve(value);
            }, 
            reject: (reason) => {
              clearTimeout(timeout);
              reject(reason);
            }
          });
        })
          .then(() => {
            originalRequest.headers = originalRequest.headers || {};
            const token = localStorage.getItem('accessToken');
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => {
            // On any error, redirect to login
            localStorage.clear();
            triggerGlobalLogout();
            window.location.href = '/login';
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token with timeout
        const refreshPromise = authService.refreshToken();
        const timeoutPromise = new Promise<boolean>((_, reject) => {
          setTimeout(() => reject(new Error('Refresh timeout')), 8000);
        });
        
        const refreshed = await Promise.race([refreshPromise, timeoutPromise]);
        
        if (refreshed) {
          processQueue(null);
          
          // Retry the original request with new token
          originalRequest.headers = originalRequest.headers || {};
          const token = localStorage.getItem('accessToken');
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        } else {
          // Refresh failed
          processQueue(error);
          localStorage.clear();
          triggerGlobalLogout();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.clear();
        triggerGlobalLogout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;